<template>
  <component
    :is="componentToShow"
    :to="cta ? cta.href : ''"
    :target="cta && cta.href && cta.href.toLowerCase().startsWith('http') ? '_blank' : ''"
    class="group relative flex h-full min-h-[512px] w-full flex-col justify-end overflow-hidden rounded-xl bg-cover text-white shadow-2xl md:aspect-h-10 md:aspect-w-19 md:flex-row md:justify-start lg:min-h-full"
    @click="handleClick">
    <!-- Wrapper for @tailwindcss/aspect-ratio padding trick -->
    <div
      class="flex h-full min-h-[512px] w-full flex-col justify-end p-6 sm:p-8 md:flex-row md:justify-start md:p-12 lg:min-h-full lg:p-16">
      <!-- Clip path for Safari mouse over effect, to clip rounded corners -->
      <div
        v-if="backgroundImage || backgroundImageMobile"
        class="absolute inset-0 h-full w-full overflow-hidden rounded-xl"
        :style="{ clipPath: 'content-box' }">
        <NuxtImg
          v-if="!isMobile"
          :alt="title || ''"
          :src="backgroundImage"
          class="h-full w-full transform object-cover transition duration-300"
          :class="cta && cta.href ? 'group-hover:scale-110' : ''"
          sizes="xs:87vw sm:87vw md:87vw lg:1140px xl:1140px xxl:1140px"
          loading="lazy" />
        <NuxtImg
          v-else
          :src="backgroundImageMobile ? backgroundImageMobile : backgroundImage"
          :alt="title || ''"
          class="h-full w-full transform object-cover object-top transition duration-300"
          :class="cta && cta.href ? 'group-hover:scale-110' : ''"
          sizes="xs:87vw sm:87vw md:87vw lg:1140px xl:1140px xxl:1140px"
          fit="inside"
          loading="lazy" />
      </div>
      <div
        class="absolute inset-0 h-full w-full transform overflow-hidden rounded-xl"
        :style="{
          background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.8) 0%, rgba(10, 10, 10, 0) 50%)',
          clipPath: 'content-box'
        }" />
      <div class="flex flex-grow flex-col justify-between">
        <div class="relative">
          <NuxtImg
            v-if="logo"
            :src="logo"
            alt="Logo"
            class="h-20 sm:h-16 lg:h-24"
            sizes="xs:200px sm:200px md:200px lg:200px xl:200px xxl:200px"
            loading="lazy" />
        </div>
        <div class="max-w-[90%] md:max-w-md lg:max-w-lg">
          <h3
            class="relative text-2xl sm:text-3xl md:text-4xl lg:text-5xl lg:leading-tight"
            v-html="title" />
          <div
            v-if="copy"
            class="relative mt-2 font-normal md:mt-6"
            v-html="copy" />
        </div>
      </div>
      <ButtonBase
        v-if="cta"
        :type="cta.type"
        :label="cta.title"
        :href-type="hrefType"
        class="pointer-events-none relative mt-8 flex flex-shrink-0 md:ml-4 md:flex-grow md:items-end md:justify-end" />
    </div>
  </component>
</template>

<script setup lang="ts">
  import { computed, resolveComponent } from 'vue'

  import { useScreenSize } from '~/composables/use-screen-size'

  import { useLinkType } from '../../composables/link-type'
  import type { CtaType } from '../../models/button'

  type CardProductProps = {
    title?: string
    copy?: string
    cta?: CtaType
    logo?: string
    backgroundImage: string
    backgroundImageMobile?: string
  }

  const props = defineProps<CardProductProps>()
  const { $event: trackEvent } = useNuxtApp()
  const { isMobile } = useScreenSize()

  const componentToShow = computed(() => {
    if (props.cta && props.cta.href && props.cta.href.length > 0) {
      return resolveComponent('NuxtLink')
    }
    return 'div'
  })

  const hrefType = computed(() => {
    if (props.cta && props.cta.href && props.cta.href.length > 0) {
      return useLinkType(props.cta.href)
    }

    return null
  })

  function handleClick() {
    trackEvent('Card Click', {
      props: {
        ['Type']: 'product',
        ['Title']: props.title,
        ['URL']: props.cta?.href,
        ['Background Image']: props.backgroundImage
      }
    })
  }
</script>
